import { useAuth } from '@/contexts/auth-context';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { needsBillingModal } from '@/libs/billing-access';
import type React from 'react';
import { isBillingFinancialRoute } from '@/config/axios.config';
import { useLocation } from 'react-router-dom';
import { SuccessFeeTermsLockedPage } from './SuccessFeeTermsLockedPage';
import { useSuccessFeeLock } from './use-success-fee-lock';

interface SuccessFeeTermsLockGateProps {
  children: React.ReactNode;
}

/**
 * Holds ordinary dashboard routes until acceptance while preserving existing
 * money, its documents, and the entry that resolves Shopify approval.
 *
 * It sits above the subscription guard on purpose: a locked organization's
 * subscription call is one of the ones the API refuses, and the guard reads a
 * failed subscription as an expired one and sends the merchant to the paywall.
 *
 * It never holds the dashboard back while the terms query is in flight. Almost
 * every session is not concerned, and a spinner in front of the whole app on
 * every load would be a heavy price for a state that is dormant most of the
 * time. A session that turns out to be locked swaps a moment later, or on the
 * first refused request, whichever comes first.
 */
export const SuccessFeeTermsLockGate: React.FC<SuccessFeeTermsLockGateProps> = ({ children }) => {
  const { isLocked, view, fallbackDeadline } = useSuccessFeeLock();
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const billing = useBillingContextQuery({ enabled: isLocked && pathname === '/dashboard' });
  const canResolveApproval =
    pathname === '/welcome/shopify' ||
    (pathname === '/dashboard' &&
      new URLSearchParams(search).get('paywall') === 'plans' &&
      !billing.error &&
      needsBillingModal(billing.data, {
        brandId: user?.currentBrandId,
        organizationId: user?.currentBrand?.organizationId,
      }));

  if (!isLocked || isBillingFinancialRoute(pathname) || canResolveApproval) return <>{children}</>;

  return <SuccessFeeTermsLockedPage view={view} fallbackDeadline={fallbackDeadline} />;
};
